import musicController from "../audio/music-controller";
import type { SoundId } from "../audio/sounds";
import { inAndroidApp } from "./app-shell";
import { getSettings, type VibrationStrength } from "./settings";

// What a press feels like, for every button in the app at once: a click sound,
// and in the Android app a short vibration. One listener on the document, so no
// button has to opt in. A button can pick its sound with data-sound="confirm",
// "back", "toggle", "buy" or "none"; checkboxes and switches sound as toggles.

const PRESSABLE = "button, a[href], [role=button], [role=switch], [role=tab], input[type=checkbox], input[type=radio], select, summary";
// What a press feels like, per Settings' vibration strength: a plain button and a
// confirm button (Play Now, Join...), as the app plays them (fx: one of Android's
// own effects, buzz:<ms> a full-strength buzz; apps/android/bridge/AppBridge.java)
// and, without the app's line, as a length for the browser's vibration.
type Haptic = { app: string; ms: number };
const HAPTICS: Record<VibrationStrength, { press: Haptic; confirm: Haptic }> = {
  light: { press: { app: "fx:click", ms: 20 }, confirm: { app: "fx:heavy", ms: 30 } },
  normal: { press: { app: "fx:heavy", ms: 30 }, confirm: { app: "buzz:45", ms: 45 } },
  strong: { press: { app: "buzz:40", ms: 40 }, confirm: { app: "buzz:70", ms: 70 } },
};
export const VIBRATION_STRENGTHS: { id: VibrationStrength; label: string }[] = [
  { id: "light", label: "Light" }, { id: "normal", label: "Normal" }, { id: "strong", label: "Strong" },
];

function target(e: Event): HTMLElement | null {
  const t = e.target as HTMLElement | null;
  const el = t && t.closest ? (t.closest(PRESSABLE) as HTMLElement | null) : null;
  if (!el || (el as HTMLButtonElement).disabled || el.getAttribute("aria-disabled") === "true") return null;
  return el;
}

// What a button does, by what it says: its label for screen readers, its tooltip,
// or (when short) its text. A way out ("Back", "Close", "Cancel", "Leave", an x)
// sounds as back; into a game ("Join", "Join next Royale", "Play again") as confirm.
const BACK_WORDS = /^(back|close|cancel|leave|exit|dismiss)\b/i;
const BACK_TEXT = /^[×✕✖x]$/i;
const CONFIRM_WORDS = /^(join|play|play again|start|find a match)\b/i;

export function soundFor(el: HTMLElement): SoundId | null {
  const named = el.closest("[data-sound]")?.getAttribute("data-sound");
  if (named === "none") return null;
  if (named === "confirm" || named === "back" || named === "toggle" || named === "buy") return ("ui-" + named) as SoundId;
  const type = (el as HTMLInputElement).type;
  // switches: a checkbox or radio, or role=switch (on/off buttons say data-sound="toggle")
  if (el.getAttribute("role") === "switch" || type === "checkbox" || type === "radio") return "ui-toggle";
  const label = (el.getAttribute("aria-label") || el.getAttribute("title") || "").trim();
  const text = (el.textContent || "").trim().replace(/\s+/g, " ");
  const says = label || (text.length <= 24 ? text : "");
  if (BACK_WORDS.test(says) || BACK_TEXT.test(text)) return "ui-back";
  if (CONFIRM_WORDS.test(says)) return "ui-confirm";
  return "ui-click";
}

// The Android app's own line for haptics (apps/android/bridge/AppBridge.java):
// a port the app sends once the page has loaded (caught in index.html). Chrome's
// navigator.vibrate does nothing on some phones; the app's vibration always
// works, so it is used whenever the line is open.
function appPort(): MessagePort | null {
  return (typeof window !== "undefined" && (window as any).__arAppPort) || null;
}
// Re-render on the app's line opening (it arrives a moment after the page).
export function onAppBridge(fn: () => void): () => void {
  window.addEventListener("ar-app-port", fn);
  return () => window.removeEventListener("ar-app-port", fn);
}

// Can this device vibrate for us? Only inside the Android app (a phone's own
// browser buzzing on every tap would surprise people), through the app's line
// or where the browser has the call.
export function canVibrate(): boolean {
  return inAndroidApp() && (!!appPort() || (typeof navigator !== "undefined" && typeof navigator.vibrate === "function"));
}

// Settings' "Test vibration": a long buzz, and what the phone answered, in words
// (to find out why a phone feels nothing).
export const VIBRATION_TEST_IDLE = "A long buzz, to check the phone allows it.";
export function testVibration(): string {
  const port = appPort();
  if (port) {
    try { port.postMessage(appVersion() >= 2 ? "buzz:200" : "test"); return "Sent to the app" + ((window as any).__arAppSeen?.hello ? "." : " (no hello from it yet)."); } catch { /* the line closed: try the browser */ }
  }
  // No line from the app: say what did arrive (index.html counts it), then try Chrome's own.
  const seen = (typeof window !== "undefined" && (window as any).__arAppSeen) || null;
  const line = inAndroidApp() && seen ? ` No line from the app (${seen.messages} message${seen.messages === 1 ? "" : "s"}, ${seen.ports} port${seen.ports === 1 ? "" : "s"}).` : "";
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return "Not available: this phone's browser has no vibration." + line;
  let sent = false;
  try { sent = navigator.vibrate(200); } catch { /* refused */ }
  return (sent ? "Sent through Chrome." : "Refused by the browser.") + line;
}

// The app's bridge version (its hello, caught in index.html): 1 knows only one
// click ("tick"), 2 the named vibrations.
const appVersion = (): number => (typeof window !== "undefined" && (window as any).__arAppSeen?.version) || 1;

let lastBuzz = 0;
function play(h: Haptic): void {
  const port = appPort();
  if (port) {
    try { port.postMessage(appVersion() >= 2 ? h.app : "tick"); lastBuzz = Date.now(); return; } catch { /* the line closed: try the browser */ }
  }
  try { if (navigator.vibrate(h.ms)) lastBuzz = Date.now(); } catch { /* not allowed right now */ }
}
// A button press: `confirm` for a button into a game.
export function buzz(confirm = false, strength?: VibrationStrength): void {
  const settings = getSettings();
  if (!canVibrate() || !settings.vibration) return;
  const plan = HAPTICS[strength || settings.vibrationStrength] || HAPTICS.normal;
  play(confirm ? plan.confirm : plan.press);
}

// Chrome only lets a page vibrate once it has had a real tap (a finger going
// down is not one yet), so a press the page wasn't allowed to buzz on yet buzzes
// on its click instead.
// (The app's line has no such rule.)
const activated = () => !!appPort() || !(navigator as any).userActivation || (navigator as any).userActivation.hasBeenActive;

let started = false;
export function startUiFeedback(): void {
  if (started || typeof document === "undefined") return;
  started = true;
  // The vibration on the press itself, so it lands with the finger.
  document.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse" || !activated()) return;
    const el = target(e);
    // (data-haptic="none": the control plays its own, like the strength samples)
    if (el && !el.closest("[data-haptic=none]")) buzz(soundFor(el) === "ui-confirm");
  }, { capture: true, passive: true });
  // The sound on the click, which a press that slides off a button never makes.
  document.addEventListener("click", (e) => {
    const el = target(e);
    if (!el) return;
    const id = soundFor(el);
    if (Date.now() - lastBuzz > 400 && !el.closest("[data-haptic=none]")) buzz(id === "ui-confirm"); // (the press couldn't: see activated)
    if (id) musicController.play(id);
  }, { capture: true, passive: true });
}
